import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  IAuthenticationResult,
  IUser,
} from '@msslib/models/authorization';
import { IAuthorizeService } from '@msslib/services/iauthorize.service';
import { AuthorizeOidcService } from '@msslib/services/authorize-oidc.service';
import { AuthorizeAngularOidc2Service } from '@msslib/services/authorize-angular-oidc2.service';

// Temporary add flag to have possibility switch to legacy OIDC authorization
const userAngularOidc2Auth = true;

@Injectable({
  providedIn: 'root',
})
export class AuthorizeService implements IAuthorizeService {
  private authService: IAuthorizeService;

  public constructor(
    authorizeOidcService: AuthorizeOidcService,
    authorizeAngularOidc2Service: AuthorizeAngularOidc2Service,
  ) {
    this.authService = userAngularOidc2Auth ? authorizeAngularOidc2Service : authorizeOidcService;
  }

  public async setupExternalLoginConfig(): Promise<void> {
    await this.authService.setupExternalLoginConfig();
  }

  public setupInternalLoginConfig(): Promise<void> {
    return this.authService.setupInternalLoginConfig();
  }

  public get allowCriteriaPlus(): boolean {
    return this.authService.allowCriteriaPlus;
  }

  public get user(): IUser | null {
    return this.authService.user;
  }

  public get isLoggedIn(): boolean {
    return this.authService.isLoggedIn;
  }

  public get tokenKey(): string {
    return this.authService.tokenKey;
  }

  public get tokenEndpoint(): string {
    return this.authService.tokenEndpoint;
  }

  public get isSsoUser$(): Observable<boolean | null> {
    return this.authService.isSsoUser$;
  }

  public get isSsoUser(): boolean {
    return this.authService.isSsoUser;
  }

  public get isS365Client(): boolean {
    return this.authService.isS365Client;
  }

  public get clientId(): string | undefined {
    return this.authService.clientId;
  }

  public get agencyNumber(): any {
    return this.authService.agencyNumber;
  }

  public get getUserInformation(): any {
    return this.authService.getUserInformation;
  }

  public get isAgencyNumberRegistered() {
    return this.authService.isAgencyNumberRegistered;
  }

  public set isAgencyNumberRegistered(value) {
    this.authService.isAgencyNumberRegistered = value;
  }

  public get historyView() {
    return this.authService.historyView;
  }

  public set historyView(value) {
    this.authService.historyView = value;
  }

  public setAgencyNumber(newAgencyNumber: string | undefined, isRegistered: boolean | null = null) {
    this.authService.setAgencyNumber(newAgencyNumber, isRegistered);
  }

  public getUserDistinct(): Observable<IUser | null> {
    return this.authService.getUserDistinct();
  }

  public setUser(user: IUser, token: string | null) {
    return this.authService.setUser(user, token);
  }

  public hasRole(role: string): boolean {
    return this.authService.hasRole(role);
  }

  public isAuthenticated(): Observable<boolean> {
    return this.authService.isAuthenticated();
  }

  public getAccessToken(): Observable<string | null> {
    return this.authService.getAccessToken();
  }

  public getUser(): Observable<IUser | null> {
    return this.authService.getUser();
  }

  public completeSignOut(state: string): Promise<IAuthenticationResult> {
    return this.authService.completeSignOut(state);
  }

  public signOut(state: unknown): Promise<IAuthenticationResult> {
    return this.authService.signOut(state);
  }

  public completeSignIn(url: string): Promise<IAuthenticationResult> {
    return this.authService.completeSignIn(url);
  }

  public signIn(state: any): Promise<IAuthenticationResult> {
    return this.authService.signIn(state);
  }
}
